export function getCurrentDate() {
  const today = new Date();

  const day = String(today.getDate()).padStart(2, "0");
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const year = String(today.getFullYear());

  return { year, month, day };
}

export function getNextMonth(month, year, monthGap = 1) {
  const currentMonthNum = parseInt(month, 10);
  const gapMonths = currentMonthNum - 1 + monthGap;

  let nextMonthNum = (gapMonths % 12) + 1;
  const yearOffset = Math.floor(gapMonths / 12);

  const nextYear = parseInt(year) + yearOffset;
  return {
    month: String(nextMonthNum).padStart(2, "0"),
    year: String(nextYear),
  };
}

export function parseStringToDate(dd_mm_yyyy) {
  if (!dd_mm_yyyy || typeof dd_mm_yyyy !== "string") {
    return null;
  }
  
  try {
    const parts = dd_mm_yyyy.split("/");
    if (parts.length !== 3) {
      return null;
    }
    
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Months are 0-indexed in JavaScript
    const year = parseInt(parts[2], 10);
    
    if (isNaN(day) || isNaN(month) || isNaN(year)) {
      return null;
    }
    
    const date = new Date(year, month, day);
    if (isNaN(date.getTime())) {
      return null;
    }
    
    return date;
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error('[DatesHelpers] Error parsing date:', error);
    }
    return null;
  }
}

export function formatDate(dateString) {
  if (!dateString) return ''; // Return empty string instead of null for backward compatibility
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return ''; // Return empty string instead of null
    }
    
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // January is 0!
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error('[DatesHelpers] Error formatting date:', error);
    }
    return ''; // Return empty string instead of null for backward compatibility
  }
}

/** Parse "DD/MM/YYYY" to Date at local noon (no UTC shift). Returns null if invalid. */
export function parseDDMMYYYYToLocal(dateStr) {
  if (!dateStr || typeof dateStr !== "string") return null;
  const parts = dateStr.trim().split("/");
  if (parts.length !== 3) return null;
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const year = parseInt(parts[2], 10);
  if (Number.isNaN(day) || Number.isNaN(month) || Number.isNaN(year)) return null;
  const d = new Date(year, month, day, 12, 0, 0);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Format Date to "DD/MM/YYYY" using local getDate/getMonth/getFullYear. */
export function formatDateIL(dateObj) {
  if (!dateObj || !(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return "";
  const day = String(dateObj.getDate()).padStart(2, "0");
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const year = dateObj.getFullYear();
  return `${day}/${month}/${year}`;
}

function datePartToken(raw) {
  if (raw == null || raw === "") return "";
  return String(raw).trim().split(/\s+/)[0].split("T")[0];
}

/**
 * Parse a customer/agent-facing date without US slash flipping.
 * Slash dates are always DD/MM/YYYY (Israel). Never `new Date("06/12/2026")`.
 * @returns {Date|null}
 */
export function parseIsraelDisplayDate(raw) {
  const s = datePartToken(raw);
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return parseIsoDatePartLocal(s);
  const slash = s.includes("/") ? s : s.replace(/-/g, "/");
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(slash)) {
    const [day, month, year] = slash.split("/");
    return parseDDMMYYYYToLocal(
      `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year}`
    );
  }
  return null;
}

/**
 * Format any quote/search date as DD/MM/YYYY for Israel display.
 * ISO 2026-12-06 → 06/12/2026. Slash 06/12/2026 stays 06/12/2026 (December 6).
 */
export function formatIsraelDisplayDate(raw) {
  const parsed = parseIsraelDisplayDate(raw);
  return parsed ? formatDateIL(parsed) : "";
}

/** Local date key "YYYY-MM-DD" for comparisons (from getFullYear/getMonth/getDate, not ISO). */
export function toLocalDateKey(dateObj) {
  if (!dateObj || !(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return "";
  const y = dateObj.getFullYear();
  const m = String(dateObj.getMonth() + 1).padStart(2, "0");
  const d = String(dateObj.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Number of nights (calendar days): departure - arrival. Returns null if invalid or departure <= arrival. */
export function diffNights(arrivalDate, departureDate) {
  if (!arrivalDate || !departureDate) return null;
  const a = arrivalDate instanceof Date ? arrivalDate : parseDDMMYYYYToLocal(String(arrivalDate));
  const b = departureDate instanceof Date ? departureDate : parseDDMMYYYYToLocal(String(departureDate));
  if (!a || !b || Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  const keyA = toLocalDateKey(a);
  const keyB = toLocalDateKey(b);
  if (!keyA || !keyB || keyB <= keyA) return null;
  const ms = b.getTime() - a.getTime();
  const days = Math.round(ms / (1000 * 60 * 60 * 24));
  return days >= 1 ? days : null;
}

/** Day name in Hebrew (local date). */
export function formatDayNameIL(dateObj) {
  if (!dateObj || !(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return "";
  return new Intl.DateTimeFormat("he-IL", { weekday: "long" }).format(dateObj);
}

/**
 * Get Hebrew day name from date string DD/MM/YYYY.
 * Returns e.g. "ראשון", "שני", ... "שבת".
 */
export function getHebrewDayName(dateStr) {
  if (!dateStr || typeof dateStr !== "string") return "";
  const d = parseDDMMYYYYToLocal(dateStr.trim());
  if (!d || Number.isNaN(d.getTime())) return "";
  return formatDayNameIL(d);
}

/**
 * Format as "יום בשבוע, DD/MM/YYYY" e.g. "ראשון, 17/05/2026"
 */
export function getHebrewDayAndDate(dateStr) {
  if (!dateStr || typeof dateStr !== "string") return "";
  const d = parseDDMMYYYYToLocal(dateStr.trim());
  if (!d || Number.isNaN(d.getTime())) return dateStr;
  const dayName = formatDayNameIL(d);
  const formatted = formatDateIL(d);
  return formatted ? `${dayName}, ${formatted}` : dateStr;
}

/** Sunday = א׳ … Saturday = ש׳ (aligns with backend hebrew_weekday_short_sunday_first). */
const HEBREW_WEEKDAY_SHORT_SUN0 = ["א׳", "ב׳", "ג׳", "ד׳", "ה׳", "ו׳", "ש׳"];

/** ISO "YYYY-MM-DD" or "YYYY-MM-DDTHH:mm…" — local noon to avoid UTC drift. */
export function parseIsoDatePartLocal(iso) {
  const seg = String(iso || "")
    .trim()
    .split("T")[0]
    .split("-");
  if (seg.length !== 3 || String(seg[0]).length !== 4) return null;
  const y = parseInt(seg[0], 10);
  const m = parseInt(seg[1], 10) - 1;
  const d = parseInt(seg[2], 10);
  if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) return null;
  const dt = new Date(y, m, d, 12, 0, 0);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/** e.g. "ו׳ 01/08" (same year context implied by caller). */
export function formatWeekdayShortDdMmHe(dateObj) {
  if (!dateObj || !(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return "";
  const wd = HEBREW_WEEKDAY_SHORT_SUN0[dateObj.getDay()];
  const day = String(dateObj.getDate()).padStart(2, "0");
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  return `${wd} ${day}/${month}`;
}

/**
 * Range label for chat (weekday + DD/MM), matching BE _he_short_date_range_label.
 * Same calendar year: no year digits; different years: include YYYY on both sides.
 */
export function formatDateRangeWithWeekdaysHe(depIso, retIso) {
  const d0 = parseIsoDatePartLocal(depIso);
  const d1 = parseIsoDatePartLocal(retIso);
  if (!d0 || !d1) return null;
  if (d0.getFullYear() === d1.getFullYear()) {
    return `${formatWeekdayShortDdMmHe(d0)} – ${formatWeekdayShortDdMmHe(d1)}`;
  }
  const w0 = HEBREW_WEEKDAY_SHORT_SUN0[d0.getDay()];
  const w1 = HEBREW_WEEKDAY_SHORT_SUN0[d1.getDay()];
  const p0 = `${String(d0.getDate()).padStart(2, "0")}/${String(d0.getMonth() + 1).padStart(2, "0")}/${d0.getFullYear()}`;
  const p1 = `${String(d1.getDate()).padStart(2, "0")}/${String(d1.getMonth() + 1).padStart(2, "0")}/${d1.getFullYear()}`;
  return `${w0} ${p0} – ${w1} ${p1}`;
}

export function getAge(birthdateString) {
  if (!birthdateString) return null;
  
  try {
    const birthdate = new Date(birthdateString);
    if (isNaN(birthdate.getTime())) {
      return null;
    }
    
    const currentDate = new Date();
    if (birthdate > currentDate) {
      return null; // תאריך עתידי
    }
    
    const ageDifference = currentDate - birthdate;
    return Math.floor(ageDifference / (1000 * 60 * 60 * 24 * 365.25));
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error('[DatesHelpers] Error calculating age:', error);
    }
    return null;
  }
}
